import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import config from 'config-base';
import AccordionArrowIcon from '../Icon/AccordionArrowIcon';
import PlusAccordionIcon from '../Icon/PlusAccordionIcon';
import styles from './Accordion.module.css';
import { AccordionIconType, AccordionProps } from './types';

const FilledLeftArrow = dynamic(() => import('../Icon/FilledLeftArrow'));

const Accordion = ({
  title,
  content,
  children,
  className,
  headerClassName,
  contentClassName,
  iconClassName,
  withoutIcon,
  titleClass,
  openIcon,
  hidePlusIcon = false,
  rippleClassName,
  onChange,
  componentRef,
  showArrowIcon,
  closeIcon,
  iconType = config.brandCode as AccordionIconType,
  ...rest
}: PropsWithChildren<AccordionProps>) => {
  const [isOpened, setIsOpened] = useState(false);
  const [height, setHeight] = useState('0px');
  const contentElement = useRef<HTMLDivElement>(null);
  const [rippleStyles, setRippleStyles] = useState<Record<string, string | number>>({});
  const rippleTimerRef = useRef<any>(0);

  const handleRipple = () => {
    rippleTimerRef.current = null;
    setRippleStyles({ opacity: 0 });
    setRippleStyles((prevState: Record<string, string | number>) => ({
      ...prevState,
      opacity: 1,
      animation: `550ms ${styles.ripple}`,
    }));
    rippleTimerRef.current = setTimeout(() => {
      setRippleStyles({ opacity: 0 });
    }, 400);
  };

  const handleOpening = () => {
    setIsOpened(!isOpened);
    if (isOpened) {
      setHeight(`${contentElement.current?.scrollHeight}px`);
      setTimeout(() => {
        setHeight('0px');
      }, 0);
    } else {
      setTimeout(() => {
        setHeight(!isOpened ? `${contentElement.current?.scrollHeight}px` : '0px');
      }, 0);

      setTimeout(() => {
        setHeight(!isOpened ? `auto` : '0px');
      }, 300);
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange(isOpened);
    }
  }, [isOpened]);

  return (
    <div className={clsx(styles.accordion, className)} {...rest}>
      <div
        data-testid={'accordion-header'}
        ref={componentRef}
        onClick={handleOpening}
        className={clsx({ [styles.active]: isOpened }, styles.accordionHeader, headerClassName)}
      >
        <div className={titleClass}>{title}</div>
        {!withoutIcon && hidePlusIcon && (
          <span className={clsx({ [styles.down]: isOpened }, styles.arrowIconContainer, iconClassName)}>
            {openIcon}
          </span>
        )}
        {showArrowIcon && <FilledLeftArrow isOpened={isOpened} />}
        {!withoutIcon && !hidePlusIcon && (
          <span
            data-testid={'accordion-ripple-trigger'}
            onClick={handleRipple}
            className={styles.rippleContainer}
            title={isOpened ? 'close' : 'open'}
          >
            <span
              className={clsx(styles.rippleElement, rippleClassName, {
                [styles.filledRipple]: iconType === 'filled',
              })}
              style={{ ...rippleStyles }}
            ></span>
            {iconType === 'filled' ? (
              <span className={clsx(styles.accordionArrowIconContainer, styles.arrowIconContainer, iconClassName)}>
                <FilledLeftArrow isOpened={isOpened} />
              </span>
            ) : null}
            {iconType === '2' ? (
              <span
                className={clsx(
                  styles.accordionArrowIconContainer,
                  styles.arrowIconContainer,
                  { [styles.accordionArrowIconContainerDown]: isOpened },
                  iconClassName,
                )}
              >
                <AccordionArrowIcon />
              </span>
            ) : null}
            {iconType === '0' ? (
              <span className={clsx('text-2xl', iconClassName)}>
                <PlusAccordionIcon
                  svgClasses={styles.accordionIcon}
                  lineClasses={clsx(styles.verticalLine, {
                    [styles.transformed]: isOpened,
                  })}
                />
              </span>
            ) : null}
          </span>
        )}
      </div>
      <div
        data-testid={'accordion-content'}
        ref={contentElement}
        style={{ height: height }}
        className={clsx(styles.accordionContent, contentClassName)}
      >
        {content
          ? isOpened && (
              <div className={styles.accordionContentContainer} dangerouslySetInnerHTML={{ __html: content }} />
            )
          : isOpened && children}
      </div>
    </div>
  );
};

export default Accordion;
